import { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import LockIcon from '@mui/icons-material/Lock';
import SettingsIcon from '@mui/icons-material/Settings';
import TerminalIcon from '@mui/icons-material/Terminal';
import Alert from '@mui/material/Alert';
import { PortalWorkflowDetailOut } from '../types/apiRequests';
import { TranslationKey } from '../i18n/en';
import CodeBlock from './CodeBlock';
import PillSelector from './PillSelector';
import StepBlock from './StepBlock';

type Platform = 'claude' | 'copilot' | 'chatgpt' | 'gemini';
type SubTab = string;

const PLATFORMS: {
  id: Platform;
  label: string;
  color: string;
  subs: { id: SubTab; label: string }[];
}[] = [
  {
    id: 'claude',
    label: 'Claude',
    color: '#D97757',
    subs: [
      { id: 'desktop', label: 'Desktop & Web' },
      { id: 'code', label: 'Claude Code' },
    ],
  },
  {
    id: 'copilot',
    label: 'Copilot',
    color: '#0078d4',
    subs: [
      { id: 'app', label: 'Microsoft 365' },
      { id: 'vscode', label: 'VS Code' },
    ],
  },
  { id: 'chatgpt', label: 'ChatGPT', color: '#10a37f', subs: [{ id: 'app', label: 'ChatGPT App' }] },
  {
    id: 'gemini',
    label: 'Gemini',
    color: '#4285f4',
    subs: [
      { id: 'app', label: 'Gemini App' },
      { id: 'cli', label: 'Gemini CLI' },
    ],
  },
];

interface WorkflowConnectTabContentProps {
  workflow: PortalWorkflowDetailOut;
  workflowMcpUrl: string;
  examplePrompt: string;
  copied: boolean;
  onCopy: (text: string) => void;
  isBeheerder: boolean;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="body2"
      sx={{ color: 'text.secondary', mb: 2, fontSize: '0.85rem', lineHeight: 1.7 }}
    >
      {children}
    </Typography>
  );
}

function InlineCode({ children }: { children: string }) {
  return (
    <code
      style={{
        background: '#f4f4f5',
        padding: '2px 6px',
        borderRadius: 4,
        fontSize: '0.78rem',
        fontFamily: "'JetBrains Mono', monospace",
      }}
    >
      {children}
    </code>
  );
}

export default function WorkflowConnectTabContent({
  workflow,
  workflowMcpUrl,
  copied,
  onCopy,
  isBeheerder,
  t,
}: WorkflowConnectTabContentProps) {
  const [platform, setPlatform] = useState<Platform>('claude');
  const platformDef = PLATFORMS.find((p) => p.id === platform)!;
  const [subTab, setSubTab] = useState<SubTab>(platformDef.subs[0].id);

  const handlePlatformChange = (p: Platform) => {
    setPlatform(p);
    setSubTab(PLATFORMS.find((pl) => pl.id === p)!.subs[0].id);
  };

  if (workflow.access_status !== 'active' && !isBeheerder) {
    return (
      <Card sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <LockIcon sx={{ fontSize: 32, color: '#d4d4d8', mb: 2 }} />
          <Typography sx={{ color: 'text.secondary', fontWeight: 500, mb: 1 }}>
            {t('workflow.connect.noAccess.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            {t('workflow.connect.noAccess.body')}
          </Typography>
        </Box>
      </Card>
    );
  }

  const desktopConfig = JSON.stringify(
    { mcpServers: { [workflow.name]: { url: workflowMcpUrl } } },
    null,
    2,
  );
  const geminiConfig = JSON.stringify(
    { mcpServers: { [workflow.name]: { url: workflowMcpUrl, transport: 'http' } } },
    null,
    2,
  );
  const copilotVsCodeConfig = JSON.stringify(
    { servers: { [workflow.name]: { type: 'http', url: workflowMcpUrl } } },
    null,
    2,
  );

  const renderContent = () => {
    // ── Claude Desktop & Web ──
    if (platform === 'claude' && subTab === 'desktop') {
      return (
        <>
          <StepBlock number={1} title={t('serverDetail.connect.step.addServer')} icon={<SettingsIcon />}>
            <Hint>{t('serverDetail.connect.claude.desktop.step1.hint')}</Hint>
            <CodeBlock
              onCopy={() => onCopy(workflowMcpUrl)}
              copied={copied}
              copyLabel={t('common.copy')}
              copiedLabel={t('common.copied')}
            >
              {workflowMcpUrl}
            </CodeBlock>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          <StepBlock number={2} title={t('serverDetail.connect.step.authenticate')} icon={<LockIcon />}>
            <Hint>{t('serverDetail.connect.claude.desktop.step2.hint')}</Hint>
          </StepBlock>
        </>
      );
    }

    // ── Claude Code ──
    if (platform === 'claude' && subTab === 'code') {
      const cmd = `claude mcp add --transport http ${workflow.name} "${workflowMcpUrl}"`;
      return (
        <>
          <StepBlock number={1} title={t('serverDetail.connect.step.addServer')} icon={<TerminalIcon />}>
            <Hint>{t('serverDetail.connect.step.runCommand')}</Hint>
            <CodeBlock
              onCopy={() => onCopy(cmd)}
              copied={copied}
              copyLabel={t('common.copy')}
              copiedLabel={t('common.copied')}
            >
              {`claude mcp add --transport http ${workflow.name} \\\n  "${workflowMcpUrl}"`}
            </CodeBlock>
            <Typography variant="body2" sx={{ color: 'text.disabled', mt: 1.5, fontSize: '0.8rem' }}>
              {t('serverDetail.connect.claude.code.step1.altHint')}{' '}
              <InlineCode>claude_desktop_config.json</InlineCode>:
            </Typography>
            <Box sx={{ mt: 1 }}>
              <CodeBlock
                onCopy={() => onCopy(desktopConfig)}
                copied={copied}
                copyLabel={t('common.copy')}
                copiedLabel={t('common.copied')}
              >
                {desktopConfig}
              </CodeBlock>
            </Box>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          <StepBlock number={2} title={t('serverDetail.connect.step.authenticate')} icon={<LockIcon />}>
            <Hint>{t('serverDetail.connect.claude.code.step2.hint')}</Hint>
          </StepBlock>
        </>
      );
    }

    // ── Copilot (Microsoft 365) ──
    if (platform === 'copilot' && subTab === 'app') {
      return (
        <>
          <StepBlock number={1} title={t('serverDetail.connect.step.addServer')} icon={<SettingsIcon />}>
            <Hint>{t('serverDetail.connect.copilot.app.step1.hint')}</Hint>
            <CodeBlock
              onCopy={() => onCopy(workflowMcpUrl)}
              copied={copied}
              copyLabel={t('common.copy')}
              copiedLabel={t('common.copied')}
            >
              {workflowMcpUrl}
            </CodeBlock>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          <StepBlock number={2} title={t('serverDetail.connect.step.authenticate')} icon={<LockIcon />}>
            <Hint>{t('serverDetail.connect.copilot.app.step2.hint')}</Hint>
          </StepBlock>
        </>
      );
    }

    // ── Copilot (VS Code) ──
    if (platform === 'copilot' && subTab === 'vscode') {
      return (
        <>
          <StepBlock number={1} title={t('serverDetail.connect.step.addServer')} icon={<SettingsIcon />}>
            <Hint>{t('serverDetail.connect.copilot.vscode.step1.hint')}</Hint>
            <CodeBlock
              onCopy={() => onCopy(copilotVsCodeConfig)}
              copied={copied}
              copyLabel={t('common.copy')}
              copiedLabel={t('common.copied')}
            >
              {copilotVsCodeConfig}
            </CodeBlock>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          <StepBlock number={2} title={t('serverDetail.connect.step.authenticate')} icon={<LockIcon />}>
            <Hint>{t('serverDetail.connect.copilot.vscode.step2.hint')}</Hint>
          </StepBlock>
        </>
      );
    }

    // ── ChatGPT App ──
    if (platform === 'chatgpt') {
      return (
        <>
          <StepBlock number={1} title={t('serverDetail.connect.step.addServer')} icon={<SettingsIcon />}>
            <Hint>{t('serverDetail.connect.chatgpt.step1.hint')}</Hint>
            <CodeBlock
              onCopy={() => onCopy(workflowMcpUrl)}
              copied={copied}
              copyLabel={t('common.copy')}
              copiedLabel={t('common.copied')}
            >
              {workflowMcpUrl}
            </CodeBlock>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          <StepBlock number={2} title={t('serverDetail.connect.step.authenticate')} icon={<LockIcon />}>
            <Hint>{t('serverDetail.connect.chatgpt.step2.hint')}</Hint>
          </StepBlock>
        </>
      );
    }

    // ── Gemini App ──
    if (platform === 'gemini' && subTab === 'app') {
      return (
        <>
          <StepBlock
            number={1}
            title={t('serverDetail.connect.gemini.app.step1.title')}
            icon={<SettingsIcon />}
          >
            <Hint>{t('serverDetail.connect.gemini.app.step1.hint')}</Hint>
            <CodeBlock
              onCopy={() => onCopy(workflowMcpUrl)}
              copied={copied}
              copyLabel={t('common.copy')}
              copiedLabel={t('common.copied')}
            >
              {workflowMcpUrl}
            </CodeBlock>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          <StepBlock number={2} title={t('serverDetail.connect.step.authenticate')} icon={<LockIcon />}>
            <Hint>{t('workflow.connect.step.loginHint')}</Hint>
          </StepBlock>
        </>
      );
    }

    // ── Gemini CLI ──
    if (platform === 'gemini' && subTab === 'cli') {
      const cmd = `gemini mcp add --transport http ${workflow.name} "${workflowMcpUrl}"`;
      return (
        <>
          <StepBlock number={1} title={t('serverDetail.connect.step.addServer')} icon={<TerminalIcon />}>
            <Hint>{t('serverDetail.connect.step.runCommand')}</Hint>
            <CodeBlock
              onCopy={() => onCopy(cmd)}
              copied={copied}
              copyLabel={t('common.copy')}
              copiedLabel={t('common.copied')}
            >
              {`gemini mcp add --transport http ${workflow.name} \\\n  "${workflowMcpUrl}"`}
            </CodeBlock>
            <Typography variant="body2" sx={{ color: 'text.disabled', mt: 1.5, fontSize: '0.8rem' }}>
              {t('serverDetail.connect.gemini.cli.step1.altHint')}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <CodeBlock
                onCopy={() => onCopy(geminiConfig)}
                copied={copied}
                copyLabel={t('common.copy')}
                copiedLabel={t('common.copied')}
              >
                {geminiConfig}
              </CodeBlock>
            </Box>
          </StepBlock>

          <Divider sx={{ my: 1 }} />

          <StepBlock number={2} title={t('serverDetail.connect.step.authenticate')} icon={<LockIcon />}>
            <Hint>{t('serverDetail.connect.gemini.cli.step2.hint')}</Hint>
          </StepBlock>
        </>
      );
    }

    return null;
  };

  return (
    <Card sx={{ overflow: 'hidden' }}>
      {workflow.access_status !== 'active' && isBeheerder && (
        <Alert severity="info" sx={{ m: 3, mb: 0 }}>
          {t('workflow.connect.inactiveAlert')}
        </Alert>
      )}
      <Box
        sx={{
          p: 3,
          pb: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          borderBottom: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        <PillSelector
          items={PLATFORMS.map((p) => ({ id: p.id, label: p.label }))}
          value={platform}
          onChange={(v) => handlePlatformChange(v as Platform)}
          color={platformDef.color}
        />
        {platformDef.subs.length > 1 && (
          <Box sx={{ display: 'flex', gap: 0, borderBottom: '1px solid transparent', mb: -0.25 }}>
            {platformDef.subs.map((sub) => (
              <Box
                key={sub.id}
                onClick={() => setSubTab(sub.id)}
                sx={{
                  'px': 2,
                  'py': 1,
                  'cursor': 'pointer',
                  'fontSize': '0.8rem',
                  'fontWeight': subTab === sub.id ? 600 : 500,
                  'color': subTab === sub.id ? 'text.primary' : 'text.disabled',
                  'borderBottom':
                    subTab === sub.id
                      ? `2px solid ${platformDef.color}`
                      : '2px solid transparent',
                  'transition': 'all 0.15s ease',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                {sub.label}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ p: 4 }}>{renderContent()}</Box>
    </Card>
  );
}
